import { Effect, Dispatch } from "react-use-elmish"

export function runFunction<Action>(f: () => void): Effect<Action> {
  return [
    () => {
      try {
        f()
      } catch {
        //
      }
    },
  ]
}

export function fromHttp<Action, Value = unknown, Err = Error>(
  promise: () => Promise<Value>,
  ofSuccess: (value: Value) => Action,
  ofError: (error: Err) => Action,
  signal?: AbortSignal,
): Effect<Action> {
  return effectFromHttp<Action, Value, Err>(promise, ofSuccess, ofError, signal)
}

export function dispatchFromHttp<Action, Err = Error>(
  promise: () => Promise<Action>,
  ofError: (error: Err) => Action,
  signal?: AbortSignal,
): Effect<Action> {
  return effectFromHttp<Action, Action, Err>(
    promise,
    (x: Action) => x,
    ofError,
    signal,
  )
}

export function attemptHttp<Action, Value = unknown, Err = unknown>(
  promise: () => Promise<Value>,
  ofError: (error: Err) => Action,
  signal?: AbortSignal,
): Effect<Action> {
  return effectFromHttp<Action, Value, Err>(promise, ofError, undefined, signal)
}

function effectFromHttp<Action, Value, Err>(
  promise: () => Promise<Value>,
  ofSuccess: ((value: Value) => Action) | ((error: Err) => Action),
  ofError?: (error: Err) => Action,
  signal?: AbortSignal,
): Effect<Action> {
  const handleError =
    ofError === undefined ? (ofSuccess as (error: Err) => Action) : ofError

  const handleSuccess =
    ofError === undefined ? undefined : (ofSuccess as (value: Value) => Action)

  return [
    (dispatch: Dispatch<Action>) =>
      promise()
        .then(value => {
          if (handleSuccess !== undefined) {
            dispatch(handleSuccess(value))
          }
        })
        .catch(error => {
          if (
            (signal == null || !signal.aborted) &&
            error.name !== "AbortError"
          ) {
            dispatch(handleError(error))
          }
        }),
  ]
}

export function delayWithCancellation<Action>(
  actionCreator: () => Action,
  timeOutMs: number,
  setTimerId: (id: number) => Action | void,
  previousTimerId: number | undefined,
): Effect<Action> {
  return [
    (dispatch: Dispatch<Action>) => {
      window.clearTimeout(previousTimerId)

      const id = window.setTimeout(() => dispatch(actionCreator()), timeOutMs)

      const action = setTimerId(id)

      if (action != null) {
        dispatch(action)
      }
    },
  ]
}
